import { useState, useEffect, useCallback } from 'react';

import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
  LOCAL_STORAGE_CHANGE_EVENT,
} from 'utils/storage';

export const useLocalStorageListener = (key: string) => {
  const getValue = useCallback(() => getLocalStorageItem(key), [key]);

  const [currentValue, setCurrentValue] = useState(getValue());

  const setValue = useCallback(
    (value: string | null) => {
      if (!value) {
        removeLocalStorageItem(key);
      } else {
        setLocalStorageItem(key, value);
      }
      setCurrentValue(value);
    },
    [key]
  );

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        setCurrentValue(event.newValue);
      }
    };

    const handleLocalStorageChange = (
      event: Event & { detail?: { key?: string; value?: string | null } }
    ) => {
      if (event.detail?.key === key) {
        setCurrentValue(event.detail.value ?? null);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(
      LOCAL_STORAGE_CHANGE_EVENT,
      handleLocalStorageChange as EventListener
    );
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(
        LOCAL_STORAGE_CHANGE_EVENT,
        handleLocalStorageChange as EventListener
      );
    };
  }, [key]);

  return { setValue, currentValue };
};
